import React,{useState}from"react";
import{X,Lock,UserCheck,Building2,Tag}from"lucide-react";
import{BLUE,GOLD}from"../config";
import{CHANGELOG,CURRENT_VERSION}from"../changelog";

const Sec=({title,children})=><div className="mb-5"><h3 className="text-xs font-bold uppercase tracking-widest mb-3" style={{color:BLUE}}>{title}</h3>{children}</div>;
const Row=({label,children})=><div className="flex gap-3 py-1.5 border-b border-slate-100 last:border-0"><p className="text-xs font-bold w-28 shrink-0 text-slate-700">{label}</p><div className="text-xs text-slate-500 flex-1">{children}</div></div>;

const TYPE_STYLE={
  release:"bg-emerald-100 text-emerald-700",
  feature:"bg-blue-100 text-blue-700",
  fix:"bg-amber-100 text-amber-700",
};

export default function HelpModal({onClose}){
  const[tab,setTab]=useState("tasks");
  const tabs=[{id:"tasks",label:"Tasks"},{id:"assign",label:"Assignment"},{id:"views",label:"Views"},{id:"tips",label:"Tips & Keys"},{id:"changelog",label:"Changelog"}];
  return(
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{background:"rgba(0,0,0,0.45)"}}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0">
          <div>
            <h2 className="text-base font-bold flex items-center gap-2" style={{color:BLUE}}>TaskHub Help Guide<span className="text-xs font-normal px-2 py-0.5 rounded-full" style={{background:"#EEF4FF",color:BLUE}}>{CURRENT_VERSION}</span></h2>
            <p className="text-xs text-slate-400">How things work & quick reference</p>
          </div>
          <button onClick={onClose} title="Close help" className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100"><X size={16}/></button>
        </div>
        <div className="flex gap-0 px-6 pt-3 border-b border-slate-100 shrink-0 overflow-x-auto">
          {tabs.map(t=><button key={t.id} onClick={()=>setTab(t.id)} className={"text-xs font-semibold px-3 py-2 border-b-2 -mb-px whitespace-nowrap "+(tab===t.id?"border-blue-600 text-blue-600":"border-transparent text-slate-500 hover:text-slate-700")}>{t.label}</button>)}
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 text-sm">
          {tab==="tasks"&&<>
            <Sec title="Status Flow">
              <div className="flex flex-wrap items-center gap-1.5 mb-3 text-xs">
                {["To Do","→","In Progress","→","Blocked","→","In Progress","→","Needs Approval","→","Completed"].map((s,i)=>
                  s==="→"?<span key={i} className="text-slate-300">→</span>
                  :<span key={i} className={"px-2 py-0.5 rounded-full font-semibold border "+
                    (s==="To Do"?"bg-slate-100 text-slate-600 border-slate-200":
                     s==="In Progress"?"bg-blue-50 text-blue-600 border-blue-200":
                     s==="Blocked"?"bg-red-50 text-red-600 border-red-200":
                     s==="Needs Approval"?"bg-purple-50 text-purple-600 border-purple-200":
                     "bg-emerald-50 text-emerald-600 border-emerald-200")}>{s}</span>
                )}
              </div>
              <div className="space-y-1.5 text-xs text-slate-600">
                <p>• Click the <strong>checkbox</strong> on a task to mark it complete (click again to undo)</p>
                <p>• <strong>Blocked</strong> — waiting on something external; add a block reason in the task detail</p>
                <p>• <strong>Needs Approval</strong> — assigned tasks go here when the assignee marks them done; owner must approve</p>
                <p>• <strong>Pending / Declined</strong> — waiting for assignee to accept, or they rejected it</p>
              </div>
            </Sec>
            <Sec title="Visibility">
              <div className="space-y-2">
                {[
                  {Icon:Lock,n:"Personal",d:"Only you. Never visible to managers or anyone else.",cl:"bg-slate-50 border-slate-200"},
                  {Icon:UserCheck,n:"Assigned",d:"Visible to you and the person you assigned it to.",cl:"bg-blue-50 border-blue-200"},
                  {Icon:Building2,n:"Company",d:"Visible to everyone. Shows in Company Tasks view.",cl:"bg-indigo-50 border-indigo-200"},
                ].map(({Icon,n,d,cl})=><div key={n} className={"flex items-start gap-3 px-3 py-2.5 rounded-lg border "+cl}><Icon size={13} className="text-slate-500 mt-0.5 shrink-0"/><div><p className="text-xs font-semibold">{n}</p><p className="text-xs text-slate-500">{d}</p></div></div>)}
              </div>
            </Sec>
            <Sec title="Priority">
              <div className="flex gap-3">
                {[
                  {p:"High",c:"bg-red-50 text-red-600 border-red-200",d:"Urgent — needs attention today"},
                  {p:"Medium",c:"bg-amber-50 text-amber-600 border-amber-200",d:"Important — complete this week"},
                  {p:"Low",c:"bg-slate-50 text-slate-500 border-slate-200",d:"Nice to have — no hard deadline"},
                ].map(({p,c,d})=><div key={p} className="flex-1"><span className={"inline-block px-2 py-0.5 rounded text-xs font-semibold border mb-1 "+c}>{p}</span><p className="text-xs text-slate-500">{d}</p></div>)}
              </div>
            </Sec>
          </>}
          {tab==="assign"&&<>
            <Sec title="How to Assign a Task">
              <div className="space-y-1.5 text-xs text-slate-600">
                <p>1. Create a task → set <strong>Assigned To</strong> → set visibility to <strong>Assigned</strong></p>
                <p>2. Assignee gets it in their <strong>Inbox</strong> with Accept / Decline buttons</p>
                <p>3. Once accepted it appears in their Work Queue and Dashboard under "Assigned to Me"</p>
                <p>4. Their Dashboard also shows it to you under "Tasks I've Assigned"</p>
              </div>
            </Sec>
            <Sec title="Acceptance Badges">
              <div className="space-y-2">
                {[
                  {b:"Pending",c:"bg-amber-50 text-amber-600 border-amber-200",d:"Waiting — assignee hasn't responded yet"},
                  {b:"Accepted",c:"bg-emerald-50 text-emerald-600 border-emerald-200",d:"Assignee agreed to take the task"},
                  {b:"Declined",c:"bg-red-50 text-red-600 border-red-200",d:"Assignee rejected it — reassign or delete from your Dashboard"},
                ].map(({b,c,d})=><div key={b} className="flex items-center gap-3 py-1"><span className={"px-2 py-0.5 rounded-full text-xs font-semibold border "+c}>{b}</span><p className="text-xs text-slate-600">{d}</p></div>)}
              </div>
            </Sec>
            <Sec title="Completion Approval Flow">
              <div className="space-y-1.5 text-xs text-slate-600">
                <p>When an assignee checks a task done → status becomes <span className="px-1.5 py-0.5 bg-purple-50 text-purple-600 border border-purple-200 rounded-full font-semibold">Needs Approval</span></p>
                <p>The task <strong>owner</strong> sees it on Dashboard under "Needs Your Approval" and can:</p>
                <p>• <strong className="text-emerald-700">Approve &amp; Close</strong> — marks it completed permanently</p>
                <p>• <strong>Reopen</strong> — sends it back to In Progress for more work</p>
                <p>Owners can also open the task detail to approve from there.</p>
              </div>
            </Sec>
            <Sec title="Returning a Task">
              <p className="text-xs text-slate-600">If you accepted a task but can no longer complete it, open the task detail and use the <strong>"Return to [Owner]"</strong> button to send it back to them.</p>
            </Sec>
          </>}
          {tab==="views"&&<>
            <Sec title="Navigation">
              <div className="divide-y divide-slate-100">
                <Row label="Dashboard">Summary stats, overdue alerts, tasks assigned to you, tasks you've assigned out, and tasks needing your approval.</Row>
                <Row label="My Day">All your active tasks grouped by visibility (personal, assigned, company-wide).</Row>
                <Row label="Work Queue">Priority-ordered queue for your daily work. Drag personal tasks to set your order. Use "Show Completed" at the bottom to see history.</Row>
                <Row label="Company Tasks">All company-wide tasks. Sort by user, priority, project, or status. Toggle to show completed tasks.</Row>
                <Row label="Inbox">Pending assignments waiting for your Accept/Decline, plus notifications when your assigned tasks change status.</Row>
                <Row label="My Team">Manager/Admin only. See all non-personal tasks for your direct reports.</Row>
                <Row label="Admin">Admin only. User management, notification settings, digest tester, impersonation.</Row>
              </div>
            </Sec>
          </>}
          {tab==="tips"&&<>
            <Sec title="Keyboard Shortcuts">
              <div className="space-y-2">
                {[
                  {k:"Ctrl K",d:"Open global search"},
                  {k:"Esc",d:"Close search / close detail panel"},
                ].map(({k,d})=><div key={k} className="flex items-center gap-3"><kbd className="px-2 py-1 bg-slate-100 rounded text-xs font-mono shrink-0 min-w-[64px] text-center">{k}</kbd><p className="text-xs text-slate-600">{d}</p></div>)}
              </div>
            </Sec>
            <Sec title="Quick Tips">
              <div className="space-y-2 text-xs text-slate-600">
                <p>💡 <strong>Search (Ctrl K)</strong> — searches task titles, descriptions, notes, and tags across everything you can see.</p>
                <p>💡 <strong>Daily Standup</strong> — managers launch full-screen standup from the sidebar. Tag tasks with the Standup toggle so they appear there.</p>
                <p>💡 <strong>Dependencies</strong> — link tasks so that dependent tasks are locked until prerequisites are completed.</p>
                <p>💡 <strong>Recurring tasks</strong> — set a task to repeat daily, weekly, or monthly. A new copy is created automatically when you complete one.</p>
                <p>💡 <strong>Tags</strong> — add free-text tags for quick filtering and search (e.g. "planning", "customer-name").</p>
                <p>💡 <strong>Notes</strong> — attach context, links, or meeting notes directly to the task so everything is in one place.</p>
                <p>💡 <strong>Act As (Admin)</strong> — in the Admin page, impersonate any user to see their exact task view for troubleshooting.</p>
                <p>💡 <strong>Daily Digest</strong> — if enabled in Admin settings, everyone gets a 7 AM email with their tasks grouped by due date horizon.</p>
                <p>💡 <strong>Completed tasks</strong> — use the "Show X completed" toggle at the bottom of Work Queue and Company Tasks to see task history.</p>
              </div>
            </Sec>
          </>}
          {tab==="changelog"&&<>
            <div className="space-y-4">
              {CHANGELOG.map((entry,i)=>(
                <div key={entry.version} className={"rounded-xl border p-4 "+(i===0?"border-blue-200 bg-blue-50":"border-slate-200 bg-white")}>
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className={"text-xs font-bold px-2 py-0.5 rounded font-mono "+(i===0?"bg-blue-600 text-white":"bg-slate-700 text-white")}>{entry.version}</span>
                    <span className={"text-xs px-2 py-0.5 rounded-full font-semibold "+(TYPE_STYLE[entry.type]||TYPE_STYLE.feature)}>{entry.type}</span>
                    <span className="text-xs text-slate-400">{entry.date}</span>
                    {i===0&&<span className="text-xs px-2 py-0.5 rounded-full font-semibold ml-auto" style={{background:GOLD,color:"#4A2800"}}>Current</span>}
                  </div>
                  <p className={"text-sm font-semibold mb-2 "+(i===0?"text-blue-800":"text-slate-700")}>{entry.title}</p>
                  <ul className="space-y-1">
                    {entry.changes.map((c,j)=>(
                      <li key={j} className="flex items-start gap-2 text-xs text-slate-600">
                        <span className="mt-1 shrink-0 w-1 h-1 rounded-full bg-slate-400 inline-block"/>
                        {c}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </>}
        </div>
        <div className="px-6 py-3 border-t border-slate-100 shrink-0 text-right">
          <button onClick={onClose} className="text-xs px-4 py-2 rounded-lg text-white font-semibold" style={{background:BLUE}}>Got it</button>
        </div>
      </div>
    </div>
  );
}
